// 2a · Mes fenêtres : groupes par lieu ou par espèce, triés par meilleur statut, compteurs,
// mini-frise J−7 → J+30, bandeau d'aide à la décision sur les fenêtres de navigation.
import { STATUS_DISPLAY, type MyWindowDto, type MyWindowsDto } from '@crenow/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { CATEGORY_LABELS, hhmm, parisDay, timelineSegment, TIMELINE_DAYS, TIMELINE_PAST, windowRelative, windowWhen } from '../format.ts';
import { useAccount } from '../session.tsx';
import { Empty, FormError, Loading, MarineBanner, Segmented, StatusPill, TabBar, useResource } from '../ui.tsx';

type Grouping = 'place' | 'species';
const GROUPING_KEY = 'crenow.grouping';
const REFRESH_MS = 10 * 60_000;

const rank = (window: MyWindowDto) => STATUS_DISPLAY[window.status].rank;

function readGrouping(): Grouping {
  try {
    return localStorage.getItem(GROUPING_KEY) === 'species' ? 'species' : 'place';
  } catch {
    return 'place';
  }
}

export function groupWindows(windows: readonly MyWindowDto[], grouping: Grouping) {
  const groups = new Map<string, { key: string; title: string; items: MyWindowDto[] }>();
  for (const window of windows) {
    const key = grouping === 'place' ? `l${window.location.id}` : window.profile.species;
    const title = grouping === 'place' ? window.location.name : window.profile.species;
    let group = groups.get(key);
    if (!group) groups.set(key, (group = { key, title, items: [] }));
    group.items.push(window);
  }
  return [...groups.values()]
    .map((group) => ({ ...group, items: [...group.items].sort((a, b) => rank(a) - rank(b) || (a.start ?? '9').localeCompare(b.start ?? '9')), rank: Math.min(...group.items.map(rank)) }))
    .sort((a, b) => a.rank - b.rank || a.title.localeCompare(b.title, 'fr'));
}

export function WindowsPage() {
  const account = useAccount();
  const { data, error, reload } = useResource<MyWindowsDto>('/windows');
  const [grouping, setGrouping] = useState<Grouping>(readGrouping);
  const [today, setToday] = useState(parisDay);

  // Les fenêtres changent à chaque synchro : relecture régulière tant que l'écran est ouvert.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setToday(parisDay());
      void reload();
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [reload]);

  function choose(next: Grouping) {
    setGrouping(next);
    try {
      localStorage.setItem(GROUPING_KEY, next);
    } catch {
      // Préférence non gardée (stockage indisponible).
    }
  }

  const windows = data?.windows ?? [];
  const count = (status: MyWindowDto['status']) => windows.filter((window) => window.status === status).length;
  const initials = account.name
    .split(/\s+/)
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="screen">
      <header className="screen-head">
        <div className="head-row baseline">
          <h1 className="h1">Mes fenêtres</h1>
          <div className="head-tools">
            {data?.updatedAt && <span className="meta">màj {hhmm(data.updatedAt)}</span>}
            <Link to="/compte" className="avatar" aria-label="Mon compte et réglages">
              {initials || '·'}
            </Link>
          </div>
        </div>
        <Segmented
          label="Regrouper les fenêtres"
          value={grouping}
          onChange={choose}
          options={[
            { value: 'place', label: 'Par lieu' },
            { value: 'species', label: 'Par espèce' },
          ]}
        />
        <p className="counters">
          <span>
            <strong className="c-run">● {count('en_cours')}</strong> en cours
          </span>
          <span>
            <strong className="c-imm">◔ {count('imminent')}</strong> imminentes
          </span>
          <span>
            <strong className="c-wait">○ {count('attente')}</strong> en attente
          </span>
        </p>
      </header>
      <main className="screen-body windows-body">
        <FormError error={error} />
        {!data && !error && <Loading />}
        {data && windows.length === 0 && (
          <Empty>
            <span>Aucune fenêtre pour l’instant. Créez un tableau : choisissez des profils et des lieux, les fenêtres arrivent à la synchro suivante.</span>
            <Link to="/creer" className="btn btn-primary btn-48">
              Créer un tableau
            </Link>
          </Empty>
        )}
        {groupWindows(windows, grouping).map((group) => (
          <section key={group.key} className="window-group">
            <div className="group-head">
              <h2 className="group-title">{group.title}</h2>
              <span className="group-meta">
                {group.items.length} {group.items.length > 1 ? 'fenêtres' : 'fenêtre'}
              </span>
            </div>
            {group.items.map((window) => (
              <WindowCard key={`${window.tableId}-${window.location.id}-${window.profile.slug}`} window={window} grouping={grouping} today={today} />
            ))}
          </section>
        ))}
      </main>
      <TabBar />
    </div>
  );
}

function WindowCard({ window, grouping, today }: { window: MyWindowDto; grouping: Grouping; today: string }) {
  const category = CATEGORY_LABELS[window.profile.category];
  const segment = timelineSegment(window, today);
  const off = window.status === 'hors_saison';
  return (
    <article className={`card window-card${off ? ' off' : ''}`}>
      <div className="window-top">
        <div className="stack">
          <span className="card-title">{grouping === 'place' ? window.profile.name : window.location.name}</span>
          <span className="card-sub">{grouping === 'place' ? category : `${category} · ${window.profile.name}`}</span>
        </div>
        <StatusPill status={window.status} />
      </div>
      <div className="window-when">
        <span className="mono">{windowWhen(window)}</span>
        <span className="muted">{windowRelative(window, today)}</span>
      </div>
      {!off && (
        <div className="timeline" aria-hidden="true">
          {segment && <span className={`timeline-window st-${STATUS_DISPLAY[window.status].token}`} style={{ left: `${segment.left}%`, width: `${segment.width}%` }} />}
          <span className="timeline-today" style={{ left: `${(TIMELINE_PAST / TIMELINE_DAYS) * 100}%` }} />
        </div>
      )}
      {window.hints.map((hint) => (
        <p key={hint.id} className="window-hint">
          {hint.text}
        </p>
      ))}
      {window.advisory && <MarineBanner />}
    </article>
  );
}
